/**
 * BRAND PAGE COMPONENT
 * 
 * Displays a brand's page - either community or verified
 * 
 * Features:
 * - Brand header with logo, name, status badge
 * - Stats (collectors, items, etc.)
 * - Ratings display
 * - Collections browser (official or aggregated)
 * - Items grid with "Add to Collection/Wishlist" buttons
 * - Follow button
 * - Claim button (for unclaimed brands)
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Timestamp, collection, getDocs, query, orderBy, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { AppIcons } from '../../constants/icons';
import { getCurrentUser } from '../../firebase/auth';
import { auth } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getBrandBySlug,
  getAggregatedCollections,
  getBrandCollections,
  followBrand,
  unfollowBrand,
  isFollowingBrand,
  getUserBrandRating
} from '../../services/brandService';
import type { Brand, BrandCollection, BrandItem, AggregatedCollection, AggregatedItem, CurationStatus } from '../../types/brand';
import BrandRatingDisplay from './BrandRatingDisplay';
import BrandRatingModal from './BrandRatingModal';
import BrandItemCard from './BrandItemCard';
import CopyToCollectionModal from './CopyToCollectionModal';
import BrandEventsSection from './BrandEventsSection';
import BrandProductDetailModal from './BrandProductDetailModal';
import { slugify } from '../../utils/polishSlug';
import { itemTypeToPath } from '../../utils/itemTypeRoutes';
import TypeBadge from '../community/TypeBadge';
import { getBrandConfirmCounts } from '../../services/polishConfirmService';

// Helper to convert Date or Timestamp to Date
const toDate = (date: Date | Timestamp | undefined): Date | null => {
  if (!date) return null;
  if (date instanceof Timestamp) return date.toDate();
  if (date instanceof Date) return date;
  return null;
};

// Helper to extract photo URL from either string or object format
// Handles: string URLs, { url: '...' } objects, and arrays of either
const getPhotoUrl = (photo: string | { url: string } | undefined): string | undefined => {
  if (!photo) return undefined;
  if (typeof photo === 'string') return photo;
  if (typeof photo === 'object' && 'url' in photo) return photo.url;
  return undefined;
};

// Helper to extract photo caption from object format
const getPhotoCaption = (photo: string | { caption?: string } | undefined): string | undefined => {
  if (!photo || typeof photo === 'string') return undefined;
  if (typeof photo === 'object' && 'caption' in photo) return photo.caption;
  return undefined;
};

// Helper to extract photo credit name from object format
const getPhotoCreditName = (photo: string | { creditName?: string } | undefined): string | undefined => {
  if (!photo || typeof photo === 'string') return undefined;
  if (typeof photo === 'object' && 'creditName' in photo) return photo.creditName;
  return undefined;
};

// Official brand product type
interface BrandProduct {
  id: string;
  name: string;
  // What kind of product this is (polish, plate, cuticle oil, etc.).
  // Existing brand-catalog records predate this field and read as
  // undefined; call sites default to 'polish' since the app was
  // polish-only when those records were created.
  itemType?: import('../../types').ItemType;
  description?: string | null;
  colorDescription?: string | null;
  category: string;
  photos: (string | { url: string; caption?: string; creditName?: string; creditUrl?: string })[];
  collectionId?: string | null;
  collectionName?: string | null;
  status: 'available' | 'coming_soon' | 'sold_out' | 'discontinued';
  price?: number | null;
  sku?: string | null;
  tags?: string[];
}

export default function BrandPage() {
  const { slug } = useParams<{ slug: string }>();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Collections & Items
  const [officialCollections, setOfficialCollections] = useState<BrandCollection[]>([]);
  const [officialProducts, setOfficialProducts] = useState<BrandProduct[]>([]);
  const [aggregatedCollections, setAggregatedCollections] = useState<AggregatedCollection[]>([]);
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  // Search across the whole Releases section. Matches on item/product name +
  // colorDescription. Uses a token-AND match so multi-word queries don't
  // require the exact phrase — "moment sunset" still finds "Moment in the
  // Sunset" because both tokens appear somewhere in the name. Short/noise
  // words are dropped so they don't inflate results.
  const [searchQuery, setSearchQuery] = useState('');
  const SEARCH_STOPWORDS = new Set([
    'a', 'an', 'the', 'in', 'on', 'of', 'to', 'and', 'or', 'for',
    'is', 'it', 'at', 'by', 'as', 'be',
  ]);
  const queryTokens = searchQuery
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !SEARCH_STOPWORDS.has(t));
  const isSearching = queryTokens.length > 0;
  // Escape regex metacharacters in user input; then build word-start
  // matchers so "hades" matches "Hades" and "Hades of Doom" but not
  // "shades" (which would sneak through a plain substring `.includes`).
  // Word-start (rather than whole-word) still allows partial typing —
  // "had" matches both "Hades" and "Hadron".
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const tokenRegexes = queryTokens.map((t) => new RegExp(`\\b${escapeRegex(t)}`, 'i'));
  const matchesQuery = (name?: string | null, colorDescription?: string | null): boolean => {
    if (!isSearching) return true;
    const haystack = `${name ?? ''} ${colorDescription ?? ''}`;
    return tokenRegexes.every((rx) => rx.test(haystack));
  };
  
  // User state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [userRating, setUserRating] = useState<any>(null);

  // Admin-only state for the curation status picker. Subscribe to auth
  // state instead of reading `getCurrentUser()` once at mount — on first
  // load, Firebase Auth often hasn't resolved yet, so a sync read returns
  // null and we'd never check admin status. The listener fires once auth
  // resolves, then again on sign-out.
  const [isAdmin, setIsAdmin] = useState(false);
  const [savingCuration, setSavingCuration] = useState(false);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setIsAdmin(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } catch {
        setIsAdmin(false);
      }
    });
    return unsub;
  }, []);

  const handleSetCurationStatus = async (next: CurationStatus | '') => {
    if (!brand) return;
    setSavingCuration(true);
    try {
      // Empty string from the <select> means "back to default" → null the field.
      // (curationStatus is optional on the type; absent means treat as community.)
      await updateDoc(doc(db, 'brands', brand.id), {
        curationStatus: next === '' ? null : next,
        updatedAt: serverTimestamp(),
      });
      setBrand({ ...brand, curationStatus: next === '' ? undefined : next });
    } catch (err) {
      console.error('Failed to set curation status:', err);
      alert('Could not save curation status. Try again.');
    } finally {
      setSavingCuration(false);
    }
  };
  
  // Modals
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [copyModalItem, setCopyModalItem] = useState<BrandItem | AggregatedItem | null>(null);
  const [confirmCounts, setConfirmCounts] = useState<Map<string, number>>(new Map());
  const [detailProduct, setDetailProduct] = useState<BrandProduct | null>(null);
  
  const currentUser = getCurrentUser();
  const isVerified = brand?.status === 'verified';
  const isCommunity = brand?.status === 'community';
  const isClaimed = brand?.status === 'claimed';
  const isOwner = currentUser && (
    brand?.claimedBy === currentUser.uid || 
    brand?.ownerId === currentUser.uid ||
    brand?.ownerEmail?.toLowerCase() === currentUser.email?.toLowerCase() ||
    brand?.claimRequest?.domainEmail?.toLowerCase() === currentUser.email?.toLowerCase()
  );

  // Load brand data
  useEffect(() => {
    async function loadBrand() {
      if (!slug) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const brandData = await getBrandBySlug(slug);
        
        if (!brandData) {
          setError('Brand not found');
          return;
        }
        
        setBrand(brandData);
        
        // Load collections based on status
        if (brandData.status === 'verified') {
          const collections = await getBrandCollections(brandData.id);
          setOfficialCollections(collections as BrandCollection[]);
          
          // Load official products
          const productsRef = collection(db, 'brands', brandData.id, 'items');
          const productsQuery = query(productsRef, orderBy('createdAt', 'desc'));
          const productsSnapshot = await getDocs(productsQuery);
          const productsData = productsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as BrandProduct[];
          setOfficialProducts(productsData);
        } else {
          const aggregated = await getAggregatedCollections(brandData.name);
          setAggregatedCollections(aggregated as AggregatedCollection[]);
          getBrandConfirmCounts(brandData.name)
            .then(setConfirmCounts)
            .catch((err) => console.error('getBrandConfirmCounts failed:', err));
        }
        
        // Check if user follows this brand
        if (currentUser) {
          const following = await isFollowingBrand(brandData.id, currentUser.uid);
          setIsFollowing(following);
          
          const rating = await getUserBrandRating(brandData.id, currentUser.uid);
          setUserRating(rating);
        }
        
      } catch (err) {
        console.error('Error loading brand:', err);
        setError('Failed to load brand');
      } finally {
        setLoading(false);
      }
    }
    
    loadBrand();
  }, [slug, currentUser?.uid]);

  // Toggle follow
  const handleFollowToggle = async () => {
    if (!brand || !currentUser || followLoading) return;
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollowBrand(brand.id, currentUser.uid);
        setIsFollowing(false);
      } else {
        await followBrand(brand.id, currentUser.uid);
        setIsFollowing(true);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  // Toggle collection expansion
  const toggleCollection = (collectionName: string) => {
    setExpandedCollections(prev => {
      const next = new Set(prev);
      if (next.has(collectionName)) {
        next.delete(collectionName);
      } else {
        next.add(collectionName);
      }
      return next;
    });
  };

  // Status badge component
  const StatusBadge = () => {
    if (isVerified) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-600/30 text-green-300 rounded-full text-sm font-medium border border-green-500/50">
          <AppIcons.BrandVerified className="w-4 h-4" />
          Verified Brand
        </span>
      );
    }
    if (isClaimed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600/30 text-amber-300 rounded-full text-sm font-medium border border-amber-500/50">
          <AppIcons.BrandPending className="w-4 h-4" />
          Verification Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-600/50 text-glass-secondary rounded-full text-sm font-medium border border-slate-500/50">
        <AppIcons.BrandCommunity className="w-4 h-4" />
        Community Page
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <AppIcons.BrandCommunity className="w-16 h-16 text-black mb-4" />
        <h1 className="text-2xl font-bold text-black mb-2">Brand Not Found</h1>
        <p className="text-black mb-4">{error || "This brand doesn't exist yet."}</p>
        <Link to="/brands" className="text-black font-medium hover:underline">
          ← Browse All Brands
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header Banner - only show if brand has uploaded one */}
      {brand.banner && (
        <div
          className="h-48 md:h-64"
          style={{
            backgroundImage: `url(${brand.banner})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      )}

      {/* Brand Info Section */}
      <div className={`max-w-6xl mx-auto px-4 relative z-10 ${brand.banner ? '-mt-16 md:-mt-20' : 'pt-8'}`}>
        <div className="glass-card p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="flex-shrink-0">
              {brand.logo ? (
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-xl object-cover border-4 border-slate-600 shadow-md"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl bg-slate-700 flex items-center justify-center border-4 border-slate-600 shadow-md">
                  <span className="text-4xl md:text-5xl font-bold text-glass">
                    {brand.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Brand Details */}
            <div className="flex-grow">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h1 className="text-2xl md:text-3xl font-bold text-glass font-heading">{brand.name}</h1>
                    <StatusBadge />
                    {/* Admin-only canonicalness curation. Not user-visible —
                        the <select> only renders when isAdmin. Verified brands
                        rank higher in brand search + autocomplete via
                        brandService.searchBrands(). */}
                    {isAdmin && (
                      <label className="flex items-center gap-2 text-xs text-glass-muted">
                        <span>Curation:</span>
                        <select
                          value={brand.curationStatus ?? ''}
                          disabled={savingCuration}
                          onChange={(e) =>
                            handleSetCurationStatus(e.target.value as CurationStatus | '')
                          }
                          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-glass font-body"
                        >
                          <option value="">(default · community)</option>
                          <option value="verified">verified · canonical</option>
                          <option value="community">community</option>
                          <option value="needs-review">needs-review</option>
                        </select>
                      </label>
                    )}
                  </div>

                  {brand.isDefunct && (
                    <p className="text-red-400 text-sm font-medium mb-2">
                      ⚠️ This brand is no longer in operation {brand.closed && `(closed ${brand.closed})`}
                    </p>
                  )}

                  {brand.description && (
                    <p className="text-glass-secondary mb-3 max-w-2xl font-body">{brand.description}</p>
                  )}

                  {/* Meta info */}
                  <div className="flex flex-wrap gap-4 text-sm text-glass-muted">
                    {brand.location && (
                      <span className="flex items-center gap-1">
                        <AppIcons.Location className="w-4 h-4" />
                        {brand.location}
                      </span>
                    )}
                    {brand.founded && (
                      <span className="flex items-center gap-1">
                        <AppIcons.Events className="w-4 h-4" />
                        Founded {brand.founded}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {currentUser && (
                    <button
                      onClick={handleFollowToggle}
                      disabled={followLoading}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        isFollowing
                          ? 'bg-slate-700 text-glass-secondary hover:bg-slate-600'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isFollowing ? (
                        <>
                          <AppIcons.Unfollow className="w-4 h-4" />
                          Following
                        </>
                      ) : (
                        <>
                          <AppIcons.Follow className="w-4 h-4" />
                          Follow
                        </>
                      )}
                    </button>
                  )}

                  {/* Claim button for unclaimed community pages */}
                  {isCommunity && currentUser && (
                    <Link
                      to={`/brands/${slug}/claim`}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                    >
                      <AppIcons.BrandPending className="w-4 h-4" />
                      Claim This Brand
                    </Link>
                  )}

                  {/* Edit button for verified brand owners */}
                  {isOwner && (
                    <Link
                      to={`/brands/${slug}/manage`}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-glass-secondary rounded-lg font-medium hover:bg-slate-600 transition-all"
                    >
                      <AppIcons.EditAction className="w-4 h-4" />
                      Manage
                    </Link>
                  )}
                </div>
              </div>

              {/* Links */}
              <div className="flex flex-wrap gap-3 mt-4">
                {brand.website && (
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-glass-secondary hover:text-glass text-sm"
                  >
                    <AppIcons.Website className="w-4 h-4" />
                    Website
                  </a>
                )}
                {brand.shopUrl && brand.shopUrl !== brand.website && (
                  <a
                    href={brand.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-glass-secondary hover:text-glass text-sm"
                  >
                    <AppIcons.ExternalLinkIcon className="w-4 h-4" />
                    Shop
                  </a>
                )}
                {brand.instagram && (
                  <a
                    href={`https://instagram.com/${brand.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-glass-secondary hover:text-glass text-sm"
                  >
                    <AppIcons.InstagramIcon className="w-4 h-4" />
                    @{brand.instagram.replace('@', '')}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-600">
            <div className="text-center">
              <div className="text-2xl font-bold text-glass">{brand.stats?.totalItems || 0}</div>
              <div className="text-sm text-glass-muted">Products</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-glass">{brand.stats?.totalCollectors || 0}</div>
              <div className="text-sm text-glass-muted">Collectors</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-glass">{brand.stats?.totalWishlisted || 0}</div>
              <div className="text-sm text-glass-muted">Wishlisted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-glass">{brand.stats?.collectionsCount || 0}</div>
              <div className="text-sm text-glass-muted">Releases</div>
            </div>
          </div>
        </div>

        {/* Ratings Section - Always show if logged in, or if ratings exist */}
        {(currentUser || brand.ratings) && (
          <div className="glass-card p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-glass">Community Ratings</h2>
              {currentUser && (
                <button
                  onClick={() => setShowRatingModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                >
                  {userRating ? '✏️ Update Your Rating' : '⭐ Rate This Brand'}
                </button>
              )}
            </div>

            {brand.ratings ? (
              <BrandRatingDisplay ratings={brand.ratings} />
            ) : (
              <div className="text-center py-8 text-glass-muted">
                <AppIcons.Wishlist className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No ratings yet</p>
                <p className="text-sm">Be the first to rate this brand!</p>
              </div>
            )}
          </div>
        )}

        {/* Upcoming Events Section */}
        {brand && (
          <BrandEventsSection
            brandId={brand.id}
            brandSlug={brand.slug}
            brandName={brand.name}
            isFollowing={isFollowing}
          />
        )}

        {/* Releases Timeline Section */}
        <div className="glass-card p-6 mt-6">
          <h2 className="text-xl font-bold text-glass mb-4">
            {isVerified ? '📅 Official Releases' : '📅 Releases Timeline'}
          </h2>

          {/* Search across every item in this brand's releases. Filters by
              name + colorDescription. When active, only collections with
              matching items render and every one that IS shown is
              auto-expanded so results are visible without extra clicks. */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search this brand — by polish name or color…"
                className="w-full bg-slate-800/60 border border-slate-600 rounded-lg pl-9 pr-9 py-2 text-glass placeholder-slate-500 focus:border-purple-500 focus:outline-none"
                aria-label="Search this brand's releases"
              />
              <svg
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {isSearching && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-glass"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {!isCommunity && !isVerified && isClaimed && (
            <p className="text-amber-400 text-sm mb-4">
              This brand page is awaiting verification. Releases shown are from community data.
            </p>
          )}

          {/* Empty search state — computed across BOTH the aggregated
              (community) and verified paths so we don't miss a hit either
              side. Renders only when a query is active and every source
              path returns zero matches. */}
          {isSearching &&
            aggregatedCollections.every((c) =>
              (c.items || []).every((i: AggregatedItem) => !matchesQuery(i.name, i.colorDescription))
            ) &&
            officialProducts.every((p) => !matchesQuery(p.name, p.colorDescription)) && (
              <div className="text-center py-8">
                <p className="text-glass-secondary">
                  No polishes match "{searchQuery.trim()}"
                </p>
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-purple-400 hover:text-purple-300 text-sm mt-2"
                >
                  Clear search
                </button>
              </div>
            )}

          {/* Flat search results — when a search is active, ignore the
              collection grouping and show matching polishes in a plain
              grid with their collection name as a small subtitle. Users
              searching for a polish name don't care about the release
              structure; the collection header was overshadowing the
              actual polish match. Collection view returns when the
              search is cleared. */}
          {isSearching && (isCommunity || isClaimed) && aggregatedCollections.length > 0 && (() => {
            const flatMatches: Array<{ item: AggregatedItem; collectionName: string }> = [];
            aggregatedCollections.forEach((c) => {
              (c.items || []).forEach((i: AggregatedItem) => {
                if (matchesQuery(i.name, i.colorDescription)) {
                  flatMatches.push({ item: i, collectionName: c.name });
                }
              });
            });
            if (flatMatches.length === 0) return null;
            return (
              <div>
                <div className="text-sm text-glass-muted mb-3">
                  {flatMatches.length} {flatMatches.length === 1 ? 'match' : 'matches'} for "{searchQuery.trim()}"
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {flatMatches.map(({ item, collectionName }) => (
                    <div key={item.id} className="relative">
                      <BrandItemCard
                        item={item}
                        brandName={brand.name}
                        brandSlug={brand.slug}
                        isCommunityItem={true}
                        confirmCount={confirmCounts.get(`${slugify(brand.name)}__${slugify(item.name)}`)}
                        onAddToCollection={() => setCopyModalItem(item)}
                        onAddToWishlist={() => setCopyModalItem({ ...item, _addAsWishlist: true } as any)}
                      />
                      {collectionName && collectionName !== 'Uncategorized' && (
                        <div className="mt-1 text-xs text-glass-muted italic">
                          in <span className="text-glass-secondary">{collectionName}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Timeline View for Community/Aggregated Collections — hidden
              during active search; the flat search-results block above
              takes over. */}
          {!isSearching && (isCommunity || isClaimed) && aggregatedCollections.length > 0 && (
            <div className="space-y-6">
              {/* Group collections by year */}
              {(() => {
                // Pre-filter aggregated collections by the active search
                // query. Collections without any matching items are dropped
                // entirely; surviving collections have their items array
                // narrowed to matches only. When no search is active this
                // is a straight passthrough.
                const filteredAggregated = isSearching
                  ? aggregatedCollections
                      .map((c) => ({
                        ...c,
                        items: (c.items || []).filter((i: AggregatedItem) =>
                          matchesQuery(i.name, i.colorDescription)
                        ),
                      }))
                      .filter((c) => (c.items || []).length > 0)
                  : aggregatedCollections;
                // Group by year
                const yearGroups = new Map<number | string, typeof aggregatedCollections>();

                filteredAggregated.forEach(collection => {
                  let yearKey: number | string;
                  
                  if (collection.isCore) {
                    yearKey = 'core';
                  } else if (collection.name === 'Uncategorized') {
                    yearKey = 'uncategorized';
                  } else {
                    yearKey = collection.year || 'unknown';
                  }
                  
                  if (!yearGroups.has(yearKey)) {
                    yearGroups.set(yearKey, []);
                  }
                  yearGroups.get(yearKey)!.push(collection);
                });
                
                // Sort years descending (newest first), with special groups at end
                const sortedYears = Array.from(yearGroups.keys()).sort((a, b) => {
                  if (a === 'core') return 1;
                  if (b === 'core') return -1;
                  if (a === 'uncategorized') return 1;
                  if (b === 'uncategorized') return -1;
                  if (a === 'unknown') return 1;
                  if (b === 'unknown') return -1;
                  return (b as number) - (a as number);
                });
                
                return sortedYears.map(yearKey => {
                  const collections = yearGroups.get(yearKey)!;
                  const yearLabel = yearKey === 'core' ? '⭐ Core / Permanent' :
                                   yearKey === 'uncategorized' ? '❓ Uncategorized' :
                                   yearKey === 'unknown' ? '📦 Unknown Year' :
                                   `📅 ${yearKey}`;
                  
                  return (
                    <div key={yearKey} className="border-l-4 border-slate-500 pl-4">
                      <h3 className="text-lg font-bold text-glass-secondary mb-3">{yearLabel}</h3>
                      <div className="space-y-3">
                        {collections.map(collection => (
                          <div key={collection.name} className="border border-slate-600 rounded-lg overflow-hidden bg-slate-700/50 backdrop-blur-sm">
                            <button
                              onClick={() => toggleCollection(collection.name)}
                              className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-600/50 transition-colors"
                            >
                              <div className="flex items-center gap-3 flex-wrap">
                                {(expandedCollections.has(collection.name) || isSearching) ? (
                                  <AppIcons.DropdownOpen className="w-5 h-5 text-glass-muted flex-shrink-0" />
                                ) : (
                                  <AppIcons.DropdownClosed className="w-5 h-5 text-glass-muted flex-shrink-0" />
                                )}

                                {/* Type icon */}
                                <span className="text-lg flex-shrink-0">
                                  {collection.isEvent ? '🎪' : collection.isCore ? '⭐' : '📁'}
                                </span>

                                {/* Release name */}
                                <span className="font-medium text-glass">{collection.name}</span>

                                {/* Item count */}
                                <span className="text-sm text-glass-muted">
                                  ({collection.itemCount} {collection.itemCount === 1 ? 'item' : 'items'})
                                </span>
                              </div>

                              {/* Type badge */}
                              {collection.releaseType && collection.releaseType !== 'Unknown' && (
                                <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                                  collection.isEvent ? 'bg-pink-600/30 text-pink-300 border border-pink-500/50' :
                                  collection.isCore ? 'bg-green-600/30 text-green-300 border border-green-500/50' :
                                  'bg-purple-600/30 text-purple-300 border border-purple-500/50'
                                }`}>
                                  {collection.isEvent ? 'Event' : collection.isCore ? 'Core' : 'Collection'}
                                </span>
                              )}
                            </button>

                            {(expandedCollections.has(collection.name) || isSearching) && (
                              <div className="p-4 border-t border-slate-600">
                                {/* Event theme callout */}
                                {collection.theme && (
                                  <div className="mb-4 p-3 bg-slate-600/50 rounded-lg">
                                    <span className="text-sm text-glass-secondary">
                                      <strong>Theme:</strong> {collection.theme}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                  {(collection.items || []).map((item: AggregatedItem) => (
                                    <BrandItemCard
                                      // Prefer id / nameSlug for React's key so the
                                      // list stays stable across renders even when
                                      // getAggregatedCollections dedupes and merges
                                      // per-user photo/finish fills into the same tile.
                                      key={item.id ?? item.nameSlug ?? item.name}
                                      item={item}
                                      brandName={brand.name}
                                      brandSlug={brand.slug}
                                      isCommunityItem={true}
                                      confirmCount={confirmCounts.get(`${slugify(brand.name)}__${slugify(item.name)}`)}
                                      onAddToCollection={() => setCopyModalItem(item)}
                                      onAddToWishlist={() => setCopyModalItem({ ...item, _addAsWishlist: true } as any)}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}

          {/* Flat search results — verified brand path. Same rationale as
              the community path above: when searching, users care about
              the polish, not the collection grouping. Renders matching
              products in a plain grid with collection name as a subtitle. */}
          {isSearching && isVerified && officialProducts.length > 0 && (() => {
            const matchingProducts = officialProducts.filter((p) =>
              matchesQuery(p.name, p.colorDescription)
            );
            if (matchingProducts.length === 0) return null;
            const collectionsById = new Map(officialCollections.map((c) => [c.id, c.name]));
            return (
              <div>
                <div className="text-sm text-glass-muted mb-3">
                  {matchingProducts.length} {matchingProducts.length === 1 ? 'match' : 'matches'} for "{searchQuery.trim()}"
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {matchingProducts.map((product) => (
                    <div key={product.id}>
                      <Link
                        to={`/brands/${brand?.slug}/${itemTypeToPath(product.itemType)}/${slugify(product.name)}`}
                        className="block bg-slate-800/50 rounded-lg overflow-hidden border border-slate-600 hover:border-purple-500/50 transition-colors"
                      >
                        <div className="relative">
                          {product.photos && product.photos.length > 0 && getPhotoUrl(product.photos[0]) ? (
                            <img
                              src={getPhotoUrl(product.photos[0])}
                              alt={product.name}
                              className="w-full aspect-square object-cover"
                            />
                          ) : (
                            <div className="w-full aspect-square bg-slate-700 flex items-center justify-center">
                              <AppIcons.ViewAll className="w-8 h-8 text-slate-500" />
                            </div>
                          )}
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/85 text-white text-xs font-medium rounded-full backdrop-blur-sm flex items-center gap-1">
                            <span aria-hidden="true">✓</span>
                            <span>Verified</span>
                          </span>
                          <TypeBadge itemType={product.itemType} />
                        </div>
                        <div className="p-3">
                          <h4 className="font-medium text-glass text-sm truncate">{product.name}</h4>
                          {product.colorDescription && (
                            <p className="text-xs text-glass-muted mt-1 line-clamp-2">{product.colorDescription}</p>
                          )}
                        </div>
                      </Link>
                      {product.collectionId && collectionsById.get(product.collectionId) && (
                        <div className="mt-1 text-xs text-glass-muted italic">
                          in <span className="text-glass-secondary">{collectionsById.get(product.collectionId)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Official Collections & Products (verified brands) — hidden
              during search; the flat search-results block above takes over. */}
          {!isSearching && isVerified && (officialCollections.length > 0 || officialProducts.length > 0) && (
            <div className="space-y-6">
              {/* Collections with their products — filter each collection's
                  products by the active search, and hide collections that
                  come up empty. Loose (unassigned) products are filtered
                  independently just below. */}
              {officialCollections
                .map(col => ({
                  col,
                  collectionProducts: officialProducts.filter(
                    (p) =>
                      p.collectionId === col.id &&
                      matchesQuery(p.name, p.colorDescription)
                  ),
                }))
                .filter(({ collectionProducts }) => !isSearching || collectionProducts.length > 0)
                .map(({ col, collectionProducts }) => {
                
                return (
                  <div key={col.id} className="border border-slate-600 rounded-lg overflow-hidden bg-slate-700/50 backdrop-blur-sm">
                    <button
                      onClick={() => toggleCollection(col.id)}
                      className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-600/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        {(expandedCollections.has(col.id) || isSearching) ? (
                          <AppIcons.DropdownOpen className="w-5 h-5 text-glass-muted" />
                        ) : (
                          <AppIcons.DropdownClosed className="w-5 h-5 text-glass-muted" />
                        )}
                        
                        {/* Collection cover image */}
                        {col.coverImage ? (
                          <img src={col.coverImage} alt={col.name} className="w-10 h-10 rounded object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-purple-600/30 flex items-center justify-center">
                            <span className="text-lg">✨</span>
                          </div>
                        )}
                        
                        <span className="font-medium text-glass">{col.name}</span>
                        
                        {col.releaseType && col.releaseType !== 'collection' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-600/30 text-purple-300 border border-purple-500/50">
                            {col.releaseType}
                          </span>
                        )}
                        
                        <span className="text-sm text-glass-muted">
                          ({collectionProducts.length} {collectionProducts.length === 1 ? 'item' : 'items'})
                        </span>
                      </div>
                      
                      {(col.releaseDate || (col.releaseSeason && col.releaseYear)) && (
                        <span className="text-sm text-glass-muted">
                          {col.releaseDate 
                            ? toDate(col.releaseDate as any)?.toLocaleDateString()
                            : `${col.releaseSeason} ${col.releaseYear}`
                          }
                        </span>
                      )}
                    </button>

                    {(expandedCollections.has(col.id) || isSearching) && (
                      <div className="p-4 border-t border-slate-600">
                        {col.description && (
                          <p className="text-glass-secondary text-sm mb-4">{col.description}</p>
                        )}
                        
                        {collectionProducts.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {collectionProducts.map(product => (
                              <Link
                                key={product.id}
                                to={`/brands/${brand?.slug}/${itemTypeToPath(product.itemType)}/${slugify(product.name)}`}
                                className="block bg-slate-800/50 rounded-lg overflow-hidden border border-slate-600 hover:border-purple-500/50 transition-colors"
                              >
                                {/* Product image with credit overlay */}
                                <div className="relative">
                                  {product.photos && product.photos.length > 0 && getPhotoUrl(product.photos[0]) ? (
                                    <img
                                      src={getPhotoUrl(product.photos[0])}
                                      alt={product.name}
                                      className="w-full aspect-square object-cover"
                                    />
                                  ) : (
                                    <div className="w-full aspect-square bg-slate-700 flex items-center justify-center">
                                      <AppIcons.ViewAll className="w-8 h-8 text-slate-500" />
                                    </div>
                                  )}

                                  {/* Verified badge — mirrors the Community pill
                                      on unclaimed brand tiles, so users have a
                                      consistent source indicator regardless of
                                      which path built the tile. */}
                                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/85 text-white text-xs font-medium rounded-full backdrop-blur-sm flex items-center gap-1">
                                    <span aria-hidden="true">✓</span>
                                    <span>Verified</span>
                                  </span>
                                  <TypeBadge itemType={product.itemType} />

                                  {/* Photo credit overlay */}
                                  {product.photos && product.photos.length > 0 && getPhotoCreditName(product.photos[0]) && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 pt-4">
                                      <span className="text-xs text-white/90">📷 {getPhotoCreditName(product.photos[0])}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Photo caption */}
                                {product.photos && product.photos.length > 0 && getPhotoCaption(product.photos[0]) && (
                                  <div className="px-3 py-1.5 bg-slate-700/50 border-b border-slate-600">
                                    <p className="text-xs text-glass-muted italic">{getPhotoCaption(product.photos[0])}</p>
                                  </div>
                                )}
                                
                                {/* Product info */}
                                <div className="p-3">
                                  <h4 className="font-medium text-glass text-sm truncate">{product.name}</h4>
                                  {product.colorDescription && (
                                    <p className="text-xs text-glass-muted mt-1 line-clamp-2">{product.colorDescription}</p>
                                  )}
                                  
                                  <div className="flex items-center justify-between mt-2">
                                    {product.price ? (
                                      <span className="text-sm font-medium text-glass">${product.price.toFixed(2)}</span>
                                    ) : (
                                      <span></span>
                                    )}
                                    
                                    {/* Status badge */}
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      product.status === 'available' ? 'bg-green-600/30 text-green-300' :
                                      product.status === 'coming_soon' ? 'bg-blue-600/30 text-blue-300' :
                                      product.status === 'sold_out' ? 'bg-red-600/30 text-red-300' :
                                      'bg-gray-600/30 text-gray-300'
                                    }`}>
                                      {product.status === 'available' ? '✅' :
                                       product.status === 'coming_soon' ? '🔜' :
                                       product.status === 'sold_out' ? 'Sold Out' : '⏹️'}
                                    </span>
                                  </div>
                                  
                                  {/* Action buttons */}
                                  {currentUser && product.status !== 'discontinued' && (
                                    <div className="flex gap-1 mt-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCopyModalItem({
                                            id: product.id,
                                            name: product.name,
                                            brand: brand?.name || '',
                                            colorDescription: product.colorDescription,
                                            photos: product.photos,
                                            category: product.category,
                                          } as any);
                                        }}
                                        className="flex-1 px-2 py-1.5 text-xs bg-purple-600/30 text-purple-300 rounded hover:bg-purple-600/50 transition-colors"
                                      >
                                        + Copy to Collection
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCopyModalItem({
                                            id: product.id,
                                            name: product.name,
                                            brand: brand?.name || '',
                                            colorDescription: product.colorDescription,
                                            photos: product.photos,
                                            category: product.category,
                                            _addAsWishlist: true,
                                          } as any);
                                        }}
                                        className="px-2 py-1.5 text-xs bg-rose-600/30 text-rose-300 rounded hover:bg-rose-600/50 transition-colors"
                                        title="Add to Wishlist"
                                      >
                                        ♡
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <p className="text-glass-muted text-sm text-center py-4">
                            No products in this collection yet.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Products not in any collection */}
              {(() => {
                const unassignedProducts = officialProducts.filter(
                  (p) =>
                    !p.collectionId && matchesQuery(p.name, p.colorDescription)
                );
                if (unassignedProducts.length === 0) return null;
                
                return (
                  <div className="border border-slate-600 rounded-lg overflow-hidden bg-slate-700/50 backdrop-blur-sm">
                    <button
                      onClick={() => toggleCollection('unassigned')}
                      className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-600/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {(expandedCollections.has('unassigned') || isSearching) ? (
                          <AppIcons.DropdownOpen className="w-5 h-5 text-glass-muted" />
                        ) : (
                          <AppIcons.DropdownClosed className="w-5 h-5 text-glass-muted" />
                        )}
                        <span className="font-medium text-glass">Other Products</span>
                        <span className="text-sm text-glass-muted">
                          ({unassignedProducts.length} {unassignedProducts.length === 1 ? 'item' : 'items'})
                        </span>
                      </div>
                    </button>

                    {(expandedCollections.has('unassigned') || isSearching) && (
                      <div className="p-4 border-t border-slate-600">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {unassignedProducts.map(product => (
                            <Link
                              key={product.id}
                              to={`/brands/${brand?.slug}/${itemTypeToPath(product.itemType)}/${slugify(product.name)}`}
                              className="block bg-slate-800/50 rounded-lg overflow-hidden border border-slate-600 hover:border-purple-500/50 transition-colors"
                            >
                              {/* Product image with credit overlay */}
                              <div className="relative">
                                {product.photos && product.photos.length > 0 && getPhotoUrl(product.photos[0]) ? (
                                  <img
                                    src={getPhotoUrl(product.photos[0])}
                                    alt={product.name}
                                    className="w-full aspect-square object-cover"
                                  />
                                ) : (
                                  <div className="w-full aspect-square bg-slate-700 flex items-center justify-center">
                                    <AppIcons.ViewAll className="w-8 h-8 text-slate-500" />
                                  </div>
                                )}

                                {/* Verified badge — parity with the other
                                    verified-brand tile locations. */}
                                <span className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/85 text-white text-xs font-medium rounded-full backdrop-blur-sm flex items-center gap-1">
                                  <span aria-hidden="true">✓</span>
                                  <span>Verified</span>
                                </span>
                                <TypeBadge itemType={product.itemType} />

                                {/* Photo credit overlay */}
                                {product.photos && product.photos.length > 0 && getPhotoCreditName(product.photos[0]) && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 pt-4">
                                    <span className="text-xs text-white/90">📷 {getPhotoCreditName(product.photos[0])}</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Photo caption */}
                              {product.photos && product.photos.length > 0 && getPhotoCaption(product.photos[0]) && (
                                <div className="px-3 py-1.5 bg-slate-700/50 border-b border-slate-600">
                                  <p className="text-xs text-glass-muted italic">{getPhotoCaption(product.photos[0])}</p>
                                </div>
                              )}
                              
                              <div className="p-3">
                                <h4 className="font-medium text-glass text-sm truncate">{product.name}</h4>
                                {product.colorDescription && (
                                  <p className="text-xs text-glass-muted mt-1 line-clamp-2">{product.colorDescription}</p>
                                )}
                                
                                <div className="flex items-center justify-between mt-2">
                                  {product.price ? (
                                    <span className="text-sm font-medium text-glass">${product.price.toFixed(2)}</span>
                                  ) : (
                                    <span></span>
                                  )}
                                  
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    product.status === 'available' ? 'bg-green-600/30 text-green-300' :
                                    product.status === 'coming_soon' ? 'bg-blue-600/30 text-blue-300' :
                                    product.status === 'sold_out' ? 'bg-red-600/30 text-red-300' :
                                    'bg-gray-600/30 text-gray-300'
                                  }`}>
                                    {product.status === 'available' ? '✅' :
                                     product.status === 'coming_soon' ? '🔜' :
                                     product.status === 'sold_out' ? 'Sold Out' : '⏹️'}
                                  </span>
                                </div>
                                
                                {currentUser && product.status !== 'discontinued' && (
                                  <div className="flex gap-1 mt-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCopyModalItem({
                                          id: product.id,
                                          name: product.name,
                                          brand: brand?.name || '',
                                          colorDescription: product.colorDescription,
                                          photos: product.photos,
                                          category: product.category,
                                        } as any);
                                      }}
                                      className="flex-1 px-2 py-1.5 text-xs bg-purple-600/30 text-purple-300 rounded hover:bg-purple-600/50 transition-colors"
                                    >
                                      + Copy to Collection
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCopyModalItem({
                                          id: product.id,
                                          name: product.name,
                                          brand: brand?.name || '',
                                          colorDescription: product.colorDescription,
                                          photos: product.photos,
                                          category: product.category,
                                          _addAsWishlist: true,
                                        } as any);
                                      }}
                                      className="px-2 py-1.5 text-xs bg-rose-600/30 text-rose-300 rounded hover:bg-rose-600/50 transition-colors"
                                      title="Add to Wishlist"
                                    >
                                      ♡
                                    </button>
                                  </div>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Empty state */}
          {aggregatedCollections.length === 0 && officialCollections.length === 0 && officialProducts.length === 0 && (
            <div className="text-center py-12 text-glass-muted">
              <AppIcons.ViewAll className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No releases found yet.</p>
              <p className="text-sm">Releases will appear as users add items from this brand.</p>
            </div>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <BrandRatingModal
          brand={brand}
          existingRating={userRating}
          onClose={() => setShowRatingModal(false)}
          onSaved={(newRating) => {
            setUserRating(newRating);
            setShowRatingModal(false);
            // Optionally refresh brand to get updated aggregate ratings
          }}
        />
      )}

      {/* Copy to Collection Modal */}
      {copyModalItem && (
        <CopyToCollectionModal
          item={copyModalItem}
          brandName={brand.name}
          asWishlist={(copyModalItem as any)._addAsWishlist || false}
          onClose={() => setCopyModalItem(null)}
          onSuccess={() => {
            setCopyModalItem(null);
            // Show success toast
          }}
        />
      )}

      {/* Product Detail Modal */}
      {detailProduct && (
        <BrandProductDetailModal
          product={detailProduct}
          brandName={brand.name}
          onClose={() => setDetailProduct(null)}
          onCopyToCollection={() => {
            setCopyModalItem({
              id: detailProduct.id,
              name: detailProduct.name,
              brand: brand?.name || '',
              colorDescription: detailProduct.colorDescription,
              photos: detailProduct.photos,
              category: detailProduct.category,
            } as any);
            setDetailProduct(null);
          }}
          onAddToWishlist={() => {
            setCopyModalItem({
              id: detailProduct.id,
              name: detailProduct.name,
              brand: brand?.name || '',
              colorDescription: detailProduct.colorDescription,
              photos: detailProduct.photos,
              category: detailProduct.category,
              _addAsWishlist: true,
            } as any);
            setDetailProduct(null);
          }}
        />
      )}
    </div>
  );
}
